import { Loader2Icon, FileTextIcon } from 'lucide-react';

import { OculomicsSearchResponse } from '@/api/ai-search';
import { cn } from '@/utils/styles';

interface StructuredAIResponseProps {
  parsedData: OculomicsSearchResponse;
  onOpenSource: (fileName: string) => void;
  previewFile: string | null;
  previewLoading: boolean;
}

export function StructuredAIResponse({
  parsedData,
  onOpenSource,
  previewFile,
  previewLoading,
}: StructuredAIResponseProps) {
  return (
    <>
      {/* Answer Section */}
      <div>
        <p className="whitespace-pre-wrap leading-relaxed">{parsedData.answer}</p>
      </div>

      {/* Key Findings Section */}
      {parsedData.key_findings && parsedData.key_findings.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-primary">Key Findings</h4>
          <ul className="space-y-2">
            {parsedData.key_findings.map((finding, idx) => (
              <li key={idx} className="flex gap-2 leading-relaxed">
                <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                <span className="flex-1">{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Supporting Quotes Section */}
      {parsedData.supporting_quotes && parsedData.supporting_quotes.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-primary">Supporting Quotes</h4>
          <div className="space-y-2">
            {parsedData.supporting_quotes.map((quote, idx) => (
              <blockquote key={idx} className="border-l-2 border-primary/40 pl-3 italic opacity-90">
                {quote}
              </blockquote>
            ))}
          </div>
        </div>
      )}

      {/* Sources Section */}
      {parsedData.sources && parsedData.sources.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-primary">Sources</h4>
          <div className="space-y-2">
            {parsedData.sources.map((source, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => void onOpenSource(source.document)}
                className={cn(
                  'w-full text-left p-2 rounded bg-background/50 hover:bg-background border border-primary/20 hover:border-primary/40 transition-colors',
                  previewLoading && previewFile === source.document
                    ? 'cursor-wait opacity-70'
                    : 'cursor-pointer'
                )}
                disabled={previewLoading && previewFile === source.document}
              >
                <div className="flex items-start gap-2">
                  {previewLoading && previewFile === source.document ? (
                    <Loader2Icon className="size-3 animate-spin mt-0.5 flex-shrink-0" />
                  ) : (
                    <FileTextIcon className="size-3 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{source.document}</div>
                    {(source.section ?? source.page) && (
                      <div className="text-[10px] opacity-70 mt-0.5">
                        {source.section && <span>Section: {source.section}</span>}
                        {source.section && source.page && <span> • </span>}
                        {source.page && <span>Page: {source.page}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Additional Context Section */}
      {parsedData.additional_context && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-primary">Additional Context</h4>
          <p className="whitespace-pre-wrap leading-relaxed opacity-90">
            {parsedData.additional_context}
          </p>
        </div>
      )}
    </>
  );
}
